import { 
  SupabaseClient, 
  RealtimeChannel, 
  REALTIME_SUBSCRIBE_STATES 
} from '@supabase/supabase-js';
import { BaseRepository } from './base.repository';
import { Document, DocumentInsert, DocumentUpdate } from '../entities/document';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { applyPagination } from '../utils/pagination.utils';

export class DocumentRepository extends BaseRepository<Document, DocumentInsert, DocumentUpdate> {
  private realtimeChannels: Map<string, RealtimeChannel> = new Map();

  constructor(supabase: SupabaseClient) {
    super(supabase, 'documents');
  }

  /**
   * Save a Document entity (create or update based on dirty state)
   */
  async save(document: Document): Promise<Document> {
    if (!document.isDirty()) {
      return document; // No changes to save
    }

    const data = document.toData();
    const { id, created_at, ...updateData } = data; // Remove fields that shouldn't be updated

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Create new entity instance from saved data
    const savedDocument = Document.fromData(saved);
    savedDocument.markAsClean();
    return savedDocument;
  }

  /**
   * Override findById to return Document entity
   */
  async findById(id: string): Promise<Document | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }
    
    return data ? Document.fromData(data) : null;
  }

  /**
   * Override create to return Document entity
   */
  async create(data: DocumentInsert): Promise<Document> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return Document.fromData(created);
  }

  async findByProject(
    projectId: string
  ): Promise<Document[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(d => Document.fromData(d));
  }

  async findByProjectPaginated(
    projectId: string, 
    options?: PaginationOptions,
    statusFilter?: 'pending' | 'approved' | 'all'
  ): Promise<PaginatedResponse<Document>> {
    let query = this.supabase
      .from(this.tableName)
      .select('*', { count: 'exact' })
      .eq('project_id', projectId);

    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    // Set default ordering if not specified
    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'created_at',
      order: options?.order || 'desc' as const
    };

    const result = await applyPagination<any, Document>(query, finalOptions, (d) => Document.fromData(d));
    
    // Ensure we always return PaginatedResponse
    if (!('pagination' in result)) {
      throw new Error('Expected paginated response');
    }
    
    return result;
  }

  /**
   * Find pending documents for a project
   */
  async findPendingByProject(projectId: string): Promise<Document[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(d => Document.fromData(d));
  }

  /**
   * Get count of pending documents
   */
  async getPendingCount(projectId: string): Promise<number> {
    const { count, error } = await this.supabase
      .from(this.tableName)
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('status', 'pending');

    if (error) throw error;
    return count || 0;
  }

  async findByCreator(creatorId: string): Promise<Document[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('created_by_id', creatorId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(d => Document.fromData(d));
  }

  async searchInProject(
    projectId: string, 
    searchTerm: string
  ): Promise<Document[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(d => Document.fromData(d));
  }

  async searchInProjectPaginated(
    projectId: string, 
    searchTerm: string,
    options?: PaginationOptions
  ): Promise<PaginatedResponse<Document>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`);

    // Set default ordering if not specified
    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'created_at',
      order: options?.order || 'desc' as const
    };

    const result = await applyPagination<any, Document>(query, finalOptions, (d) => Document.fromData(d));
    
    // Ensure we always return PaginatedResponse
    if (!('pagination' in result)) {
      throw new Error('Expected paginated response');
    }
    
    return result;
  }

  /**
   * Subscribe to real-time document updates for a project
   */
  subscribeToProjectDocuments(
    projectId: string,
    callbacks: {
      onConnect?: (status: REALTIME_SUBSCRIBE_STATES, err?: Error) => void;
      onInsert?: (document: Document) => void;
      onUpdate?: (document: Document) => void;
      onDelete?: (id: string) => void;
    },
  ): () => void {
    // Unsubscribe from existing channel if any
    this.unsubscribeFromProject(projectId);

    const channel = this.supabase
      .channel(`documents:project:${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "documents",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (callbacks.onInsert) {
            callbacks.onInsert(Document.fromData(payload.new));
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "documents",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (callbacks.onUpdate) {
            callbacks.onUpdate(Document.fromData(payload.new));
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "documents",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (callbacks.onDelete) {
            callbacks.onDelete(payload.old.id);
          }
        },
      )
      .subscribe(callbacks.onConnect);

    this.realtimeChannels.set(projectId, channel);

    // Return unsubscribe function
    return () => this.unsubscribeFromProject(projectId);
  }

  /**
   * Unsubscribe from real-time updates for a project
   */
  unsubscribeFromProject(projectId: string): void {
    const channel = this.realtimeChannels.get(projectId);
    if (channel) {
      this.supabase.removeChannel(channel);
      this.realtimeChannels.delete(projectId);
    }
  }

  /**
   * Unsubscribe from all real-time channels
   */
  unsubscribeAll(): void {
    this.realtimeChannels.forEach((channel) => {
      this.supabase.removeChannel(channel);
    });
    this.realtimeChannels.clear();
  }

}